import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPage, updatePage, createPage, getLayouts, getLayout } from 'api/pages';
import Container from '@mui/material/Container';
import MKBox from 'components/MaterialKit/MKBox';
import EditPageForm from 'pages/EditPage/EditPageForm';
import SectionList from 'pages/EditPage/SectionList';
import { handleErrorResponse } from 'utils/general';
import { useAuth } from 'contexts/auth';
import { getSectionDefinitions, getSections } from 'api/sections';
import { getMenus } from 'api/menus';
import { createLayout, updateLayout } from 'api/layouts';
import EditLayoutForm from './EditLayoutForm';

const EditLayoutPage = () => {
  const [layout, setLayout] = useState([]);
  const { layoutId } = useParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [headers, setHeaders] = useState([]);
  const [footers, setFooters] = useState([]);
  const [menus, setMenus] = useState([]);

  const fetchLayoutFromApi = useCallback(() => {
    if (!layoutId) {
      return Promise.resolve();
    }
    const layoutParams = {
      $expand: 'menu,header/section_definition/collection_definition,footer/section_definition/collection_definition',
    };
    return getLayout(layoutId, layoutParams)
      .then(({ data }) => {
        setLayout(data);
      });
  }, [layoutId]);

  const onClickCancel = useCallback(() => {
    return navigate(-1);
  }, [navigate]);

  const onClickSaveToApi = useCallback((values, { setFieldError }) => {
    const layoutBody = {
      name: values.name,
      menu: values.menu,
      footer: values.footer,
      header: values.header,
    };
    return (layoutId ? updateLayout(layoutId, layoutBody) : createLayout(layoutBody))
      .then(({ data }) => {
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', err.message);
      });
  }, [layoutId, navigate]);

  const fetchHeaderSectionsFromApi = useCallback(() => {
    const sectionParams = {
      '$expand': 'section_definition/collection_definition',
      'section_definition/collection_definition/name[like]': '%page header%',
    };
    return getSections(sectionParams)
      .then(({ data }) => {
        setHeaders(data.map((datum) => ({ section_id: datum.section_id, name: datum.section_definition.collection_definition.name })));
      });
  }, []);

  const fetchFooterSectionsFromApi = useCallback(() => {
    const sectionParams = {
      '$expand': 'section_definition/collection_definition',
      'section_definition/collection_definition/name[like]': '%page footer%',
    };
    return getSections(sectionParams)
      .then(({ data }) => {
        setFooters(data.map((datum) => ({ section_id: datum.section_id, name: datum.section_definition.collection_definition.name })));
      });
  }, []);

  const fetchMenusFromApi = useCallback(() => {
    const menuParams = {};
    return getMenus(menuParams)
      .then(({ data }) => {
        setMenus(data);
      });
  }, []);

  useEffect(() => {
    Promise.all([
      fetchLayoutFromApi(),
      fetchHeaderSectionsFromApi(),
      fetchFooterSectionsFromApi(),
      fetchMenusFromApi(),
    ]).catch((err) => {
      handleErrorResponse(err, setAuth);
    });
  }, [fetchLayoutFromApi, fetchHeaderSectionsFromApi, fetchFooterSectionsFromApi, setAuth, fetchMenusFromApi]);

  return (
    <MKBox flex={1} bgColor="light" py="2rem">
      <Container>
        <EditLayoutForm
          layout={layout}
          headers={headers}
          footers={footers}
          menus={menus}
          onSubmit={onClickSaveToApi}
          onCancel={onClickCancel}
        />
      </Container>
    </MKBox>
  );
};

export default EditLayoutPage;
